import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { auth } from '../services';
import { normalizeRole, formatRoleLabel } from '../utils/roles';
import { useTheme } from '../ThemeContext';

const NAV_ICONS = {
  DB: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" /><rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" /><rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  ),
  US: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  ST: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 10v6M2 10l10-5 10 5-10 5z" /><path d="M6 12v5c0 1.1 2.7 2 6 2s6-.9 6-2v-5" />
    </svg>
  ),
  BT: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" /><path d="M2 17l10 5 10-5" /><path d="M2 12l10 5 10-5" />
    </svg>
  ),
  AT: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
    </svg>
  ),
  PY: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="5" width="20" height="14" rx="2" /><path d="M2 10h20" />
    </svg>
  ),
  RP: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18" /><path d="M7 16l4-4 4 4 5-6" />
    </svg>
  ),
  IN: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <path d="M14 2v6h6" /><path d="M16 13H8" /><path d="M16 17H8" /><path d="M10 9H8" />
    </svg>
  ),
};

const ADMIN_NAV = [
  {
    group: 'Overview',
    items: [
      { path: '/dashboard', icon: 'DB', label: 'Dashboard', badge: null },
      { path: '/admin/users', icon: 'US', label: 'Users', badge: null, accent: '#64748b' },
    ],
  },
  {
    group: 'Academic',
    items: [
      { path: '/admin/students', icon: 'ST', label: 'Students', badge: null, accent: '#60A5FA' },
      { path: '/admin/batches', icon: 'BT', label: 'Batches', badge: null, accent: '#06B6D4' },
      { path: '/admin/attendance', icon: 'AT', label: 'Attendance', badge: null, accent: '#8B5CF6' },
    ],
  },
  {
    group: 'Finance',
    items: [
      { path: '/admin/payments', icon: 'PY', label: 'Payments', badge: null, accent: '#10B981' },
    ],
  },
  {
    group: 'Reports',
    items: [
      { path: '/admin/reports', icon: 'RP', label: 'Reports', badge: null, accent: '#F472B6' },
    ],
  },
];

const INSTRUCTOR_NAV = [
  {
    group: 'Instructor',
    items: [
      { path: '/instructor', icon: 'DB', label: 'Dashboard', badge: null, accent: '#60A5FA' },
      { path: '/instructor/attendance', icon: 'AT', label: 'Attendance', badge: null, accent: '#8B5CF6' },
      { path: '/instructor/reports', icon: 'RP', label: 'Reports', badge: null, accent: '#F472B6' },
    ],
  },
];

const RECEPTION_NAV = [
  {
    group: 'Reception',
    items: [
      { path: '/reception/students', icon: 'ST', label: 'Students', badge: null, accent: '#60A5FA' },
      { path: '/reception/payments', icon: 'PY', label: 'Payments', badge: null, accent: '#10B981' },
    ],
  },
];

const FINANCE_NAV = [
  {
    group: 'Finance',
    items: [
      { path: '/finance/payments', icon: 'PY', label: 'Payments', badge: null, accent: '#10B981' },
      { path: '/finance/invoices', icon: 'IN', label: 'Invoices', badge: null, accent: '#34D399' },
      { path: '/finance/reports', icon: 'RP', label: 'Reports', badge: null, accent: '#F472B6' },
    ],
  },
];

const TrainHubLayout = ({ children }) => {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [currentUser, setCurrentUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchFocused, setSearchFocused] = useState(false);

  useEffect(() => {
    if (auth.isAuthenticated()) {
      const user = auth.getUser();
      setCurrentUser(user);
    }
  }, []);

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin' || location.pathname === '/admin/';
    if (path === '/instructor') return location.pathname === '/instructor' || location.pathname === '/instructor/';
    if (path === '/reception') return location.pathname === '/reception' || location.pathname === '/reception/';
    if (path === '/finance') return location.pathname === '/finance' || location.pathname === '/finance/';
    return location.pathname.startsWith(path);
  };

  const handleNav = (path) => {
    navigate(path);
    setSidebarOpen(false);
  };

  const handleLogout = () => {
    auth.clearAuth();
    setCurrentUser(null);
    window.location.replace('/login');
  };

  const userRole = normalizeRole(currentUser?.role);
  const currentNav = (() => {
    if (userRole === 'instructor') return INSTRUCTOR_NAV;
    if (userRole === 'reception') return RECEPTION_NAV;
    if (userRole === 'finance') return FINANCE_NAV;
    return ADMIN_NAV;
  })();
  const activeItem = currentNav.flatMap(g => g.items).find(i => isActive(i.path));

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,wght@0,300;0,400;0,500;0,600;1,400&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

        /* ══════════════════════════════════════
           ROOT SHELL
        ══════════════════════════════════════ */
        .th-shell {
          display: flex; min-height: 100vh;
          background: var(--bg);
          color: var(--text);
          font-family: var(--font-body);
          position: relative;
        }

        /* ══════════════════════════════════════
           MOBILE OVERLAY
        ══════════════════════════════════════ */
        .th-overlay {
          display: none; position: fixed; inset: 0;
          background: rgba(0,0,0,0.65);
          backdrop-filter: blur(6px);
          z-index: 90;
          animation: thFadeIn 0.2s ease;
        }
        .th-overlay.open { display: block; }
        @keyframes thFadeIn { from { opacity:0; } to { opacity:1; } }

        /* ══════════════════════════════════════
           SIDEBAR — redesigned rail
        ══════════════════════════════════════ */
        .th-sidebar {
          width: var(--sidebar-w);
          padding: 14px 12px;
          display: flex;
          flex-direction: column;
          position: sticky;
          top: 0;
          height: 100vh;
          flex-shrink: 0;
          z-index: 100;
          transition: width var(--transition), transform var(--transition), padding var(--transition);
          overflow: hidden;
          background: transparent;
        }
        .th-sidebar.collapsed {
          width: var(--sidebar-w-col);
          padding: 14px 10px;
        }

        .th-sidebar-panel {
          flex: 1;
          min-height: 0;
          display: flex;
          flex-direction: column;
          background: linear-gradient(180deg, var(--surface) 0%, var(--surface2) 100%);
          border: 1px solid var(--border-md);
          border-radius: 20px;
          box-shadow: var(--shadow);
          overflow: hidden;
          position: relative;
        }
        .th-sidebar-panel::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, var(--accent), var(--purple), var(--green));
          opacity: 0.85;
        }

        /* ── Brand ── */
        .th-brand {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 20px 16px 14px;
          flex-shrink: 0;
        }
        .th-brand-mark {
          width: 42px;
          height: 42px;
          border-radius: 14px;
          flex-shrink: 0;
          background: var(--surface3);
          display: flex;
          align-items: center;
          justify-content: center;
          border: 1px solid var(--border-md);
          box-shadow: inset 0 1px 0 rgba(255,255,255,0.06);
        }
        .th-brand-logo {
          width: 30px;
          height: 30px;
          object-fit: contain;
        }
        .th-brand-text {
          overflow: hidden;
          transition: opacity var(--transition), width var(--transition);
          min-width: 0;
        }
        .th-sidebar.collapsed .th-brand-text { opacity: 0; width: 0; pointer-events: none; }
        .th-brand-name {
          font-family: var(--font-head);
          font-size: 17px;
          font-weight: 800;
          letter-spacing: -0.4px;
          white-space: nowrap;
          color: var(--text);
          line-height: 1.15;
        }
        .th-brand-name em { font-style: normal; color: var(--accent); }
        .th-brand-tagline {
          font-size: 10px;
          color: var(--text2);
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          margin-top: 3px;
          white-space: nowrap;
        }

        /* ── User strip ── */
        .th-user-strip {
          display: flex;
          align-items: center;
          gap: 10px;
          margin: 0 12px 12px;
          padding: 10px 12px;
          border-radius: 14px;
          background: var(--surface3);
          border: 1px solid var(--border);
          flex-shrink: 0;
          overflow: hidden;
          transition: opacity var(--transition), height var(--transition), margin var(--transition), padding var(--transition);
        }
        .th-sidebar.collapsed .th-user-strip {
          margin: 0 8px 10px;
          padding: 8px;
          justify-content: center;
        }
        .th-user-av {
          width: 36px;
          height: 36px;
          border-radius: 12px;
          flex-shrink: 0;
          background: linear-gradient(135deg, var(--accent), var(--purple));
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 14px;
          color: white;
          font-family: var(--font-head);
          position: relative;
          box-shadow: 0 4px 12px var(--accent-glow);
        }
        .th-user-av-online {
          position: absolute;
          bottom: -1px;
          right: -1px;
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: var(--green);
          border: 2px solid var(--surface3);
        }
        .th-user-info {
          flex: 1;
          min-width: 0;
          overflow: hidden;
          transition: opacity var(--transition), width var(--transition);
        }
        .th-sidebar.collapsed .th-user-info { opacity: 0; width: 0; display: none; }
        .th-user-name {
          font-size: 13px;
          font-weight: 700;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          color: var(--text);
        }
        .th-user-role {
          display: inline-flex;
          margin-top: 4px;
          font-size: 9px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.08em;
          color: var(--accent);
          background: var(--accent-glow);
          border: 1px solid rgba(59,130,246,0.2);
          padding: 2px 8px;
          border-radius: 999px;
        }

        /* Sidebar toggle */
        .th-sidebar-toggle {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 34px;
          height: 34px;
          border-radius: 10px;
          border: 1px solid var(--border);
          background: var(--surface2);
          color: var(--text2);
          cursor: pointer;
          flex-shrink: 0;
          transition: all 0.2s;
        }
        .th-sidebar-toggle:hover {
          border-color: var(--accent);
          color: var(--accent);
          background: var(--accent-glow);
        }

        /* ── Nav ── */
        .th-nav {
          flex: 1;
          overflow-y: auto;
          overflow-x: hidden;
          padding: 4px 10px 12px;
        }
        .th-nav::-webkit-scrollbar { width: 4px; }
        .th-nav::-webkit-scrollbar-thumb {
          background: var(--border-md);
          border-radius: 4px;
        }

        .th-nav-group { margin-bottom: 18px; }
        .th-nav-group-label {
          font-size: 10px;
          font-weight: 700;
          text-transform: uppercase;
          letter-spacing: 0.12em;
          color: var(--muted);
          padding: 0 10px 8px;
          white-space: nowrap;
          overflow: hidden;
          transition: opacity var(--transition);
        }
        .th-sidebar.collapsed .th-nav-group-label {
          opacity: 0;
          height: 0;
          padding: 0;
          margin: 0;
          overflow: hidden;
        }

        .th-nav-item {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 10px 12px;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.2s ease;
          font-size: 13px;
          font-weight: 500;
          color: var(--text2);
          border: none;
          background: transparent;
          width: 100%;
          text-align: left;
          margin-bottom: 4px;
          position: relative;
          white-space: nowrap;
        }
        .th-sidebar.collapsed .th-nav-item {
          justify-content: center;
          padding: 10px 8px;
        }
        .th-nav-item:hover {
          background: var(--surface3);
          color: var(--text);
          transform: translateX(2px);
        }
        .th-sidebar.collapsed .th-nav-item:hover { transform: none; }
        .th-nav-item.active {
          background: linear-gradient(135deg, var(--accent), color-mix(in srgb, var(--accent) 70%, var(--purple)));
          color: #fff;
          font-weight: 600;
          box-shadow: 0 8px 20px var(--accent-glow);
        }
        .th-nav-item.active:hover { transform: none; }

        .th-nav-icon {
          width: 34px;
          height: 34px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          transition: all 0.2s;
          background: var(--surface3);
          border: 1px solid var(--border);
          color: var(--item-accent, var(--text2));
        }
        .th-nav-icon svg {
          width: 17px;
          height: 17px;
        }
        .th-nav-item:hover .th-nav-icon {
          border-color: var(--border-md);
          color: var(--item-accent, var(--accent));
        }
        .th-nav-item.active .th-nav-icon {
          background: rgba(255,255,255,0.18);
          border-color: rgba(255,255,255,0.25);
          color: #fff;
        }

        .th-nav-label {
          flex: 1;
          overflow: hidden;
          transition: opacity var(--transition), width var(--transition);
        }
        .th-sidebar.collapsed .th-nav-label { opacity: 0; width: 0; display: none; }

        .th-nav-badge {
          background: var(--gold);
          color: #fff;
          font-size: 9px;
          font-weight: 800;
          padding: 2px 7px;
          border-radius: 999px;
          flex-shrink: 0;
        }
        .th-sidebar.collapsed .th-nav-badge { display: none; }

        .th-nav-item[data-tip] { position: relative; }
        .th-sidebar.collapsed .th-nav-item[data-tip]:hover::after {
          content: attr(data-tip);
          position: absolute;
          left: calc(100% + 12px);
          top: 50%;
          transform: translateY(-50%);
          background: var(--surface);
          border: 1px solid var(--border-md);
          color: var(--text);
          font-size: 12px;
          font-weight: 600;
          padding: 8px 12px;
          border-radius: 10px;
          white-space: nowrap;
          z-index: 200;
          box-shadow: var(--shadow-lg);
        }

        /* ── Sidebar footer ── */
        .th-sidebar-footer {
          border-top: 1px solid var(--border);
          padding: 12px;
          flex-shrink: 0;
          background: color-mix(in srgb, var(--surface2) 80%, transparent);
        }

        .th-logout-btn {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 11px 14px;
          border-radius: 12px;
          border: 1px solid var(--border-md);
          background: var(--surface3);
          color: var(--text2);
          font-family: var(--font-body);
          font-size: 13px;
          font-weight: 600;
          letter-spacing: 0.01em;
          cursor: pointer;
          transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease;
        }
        .th-sidebar.collapsed .th-logout-btn {
          padding: 11px 8px;
        }
        .th-logout-btn:hover {
          background: rgba(239, 68, 68, 0.08);
          border-color: rgba(239, 68, 68, 0.35);
          color: var(--red);
          box-shadow: 0 4px 14px rgba(239, 68, 68, 0.12);
        }
        .th-logout-btn:active {
          transform: scale(0.99);
        }
        .th-logout-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        .th-logout-icon svg {
          width: 18px;
          height: 18px;
        }
        .th-logout-label {
          white-space: nowrap;
          transition: opacity var(--transition), width var(--transition);
        }
        .th-sidebar.collapsed .th-logout-label {
          display: none;
        }

        .th-user-card { display: none; }
        .th-datetime { display: none; }

        /* ══════════════════════════════════════
           MAIN AREA
        ══════════════════════════════════════ */
        .th-main { flex: 1; min-width: 0; display: flex; flex-direction: column; overflow: hidden; }

        /* ── Topbar ── */
        .th-topbar {
          height: var(--topbar-h);
          display: flex; align-items: center; gap: 14px;
          padding: 0 24px;
          background: var(--surface);
          border-bottom: 1px solid var(--border);
          position: sticky; top: 0; z-index: 80; flex-shrink: 0;
        }

        /* Hamburger */
        .th-hamburger {
          display: none; width: 38px; height: 38px;
          border: 1px solid var(--border); background: var(--surface2);
          border-radius: var(--radius-xs); color: var(--text2);
          font-size: 18px; cursor: pointer;
          align-items: center; justify-content: center; flex-shrink: 0;
          transition: all 0.2s;
        }
        .th-hamburger:hover { border-color: var(--border-md); color: var(--text); }

        /* Breadcrumb */
        .th-breadcrumb {
          display: flex; align-items: center; gap: 6px;
          font-size: 13px; color: var(--text2);
          overflow: hidden;
        }
        .th-breadcrumb-sep { color: var(--muted); }
        .th-breadcrumb-active {
          font-family: var(--font-head); font-weight: 700; font-size: 14px;
          color: var(--text);
          display: flex; align-items: center; gap: 6px;
          white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
        }
        .th-breadcrumb-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          min-width: 22px;
          height: 22px;
          padding: 0 6px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 700;
          letter-spacing: 0.05em;
          color: #cbd5e1;
          background: var(--surface3);
          border: 1px solid var(--border);
        }

        /* Search */
        .th-search {
          flex: 1; max-width: 400px; margin-left: auto;
          display: flex; align-items: center; gap: 10px;
          background: var(--surface2); border: 1px solid var(--border);
          border-radius: var(--radius-sm); padding: 9px 14px;
          transition: all 0.2s;
        }
        .th-search.focused { border-color: rgba(59,130,246,0.45); background: var(--surface3); box-shadow: 0 0 0 3px rgba(59,130,246,0.1); }
        .th-search-icon { font-size: 14px; color: var(--muted); flex-shrink: 0; }
        .th-search input {
          flex: 1; border: none; outline: none; background: none;
          font-size: 13px; color: var(--text); font-family: var(--font-body);
        }
        .th-search input::placeholder { color: var(--muted); }
        .th-search-kbd {
          font-size: 10px; color: var(--muted); border: 1px solid var(--border);
          border-radius: 4px; padding: 2px 5px; font-weight: 600; flex-shrink: 0;
          background: var(--surface2);
        }

        /* Topbar actions */
        .th-topbar-right { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
        .th-icon-btn {
          width: 38px; height: 38px;
          border: 1px solid var(--border); background: var(--surface2);
          border-radius: var(--radius-xs); color: var(--text2);
          font-size: 17px; cursor: pointer;
          display: flex; align-items: center; justify-content: center;
          transition: all 0.2s; position: relative;
        }
        .th-icon-btn:hover { border-color: var(--border-md); color: var(--text); background: var(--surface3); }
        .th-notif-dot {
          position: absolute; top: 7px; right: 7px;
          width: 7px; height: 7px; border-radius: 50%;
          background: #EF4444; border: 1.5px solid var(--surface);
          animation: thPing 2s ease-in-out infinite;
        }
        @keyframes thPing { 0%,100% { transform:scale(1); opacity:1; } 50% { transform:scale(1.3); opacity:0.7; } }

        /* Divider */
        .th-topbar-div { width: 1px; height: 24px; background: var(--border); flex-shrink: 0; }

        /* User pill */
        .th-topbar-user {
          display: flex; align-items: center; gap: 9px;
          padding: 5px 12px 5px 5px;
          background: var(--surface2); border: 1px solid var(--border);
          border-radius: 40px; cursor: pointer; transition: all 0.2s;
        }
        .th-topbar-user:hover { border-color: var(--border-md); background: var(--surface3); }
        .th-topbar-av {
          width: 30px; height: 30px; border-radius: 50%;
          background: linear-gradient(135deg, #D97706, #EA580C);
          display: flex; align-items: center; justify-content: center;
          font-weight: 800; font-size: 12px; color: white; font-family: var(--font-head);
          flex-shrink: 0;
        }
        .th-topbar-name { font-size: 12px; font-weight: 700; white-space: nowrap; }
        .th-topbar-chevron { font-size: 10px; color: var(--muted); }

        /* ── Content ── */
        .th-content {
          flex: 1; overflow-y: auto;
          background: var(--bg);
        }
        .th-content::-webkit-scrollbar { width: 5px; }
        .th-content::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.08); border-radius: 10px; }

        /* ══════════════════════════════════════
           RESPONSIVE
        ══════════════════════════════════════ */
        @media (max-width: 1024px) {
          .th-sidebar {
            position: fixed;
            top: 0;
            left: 0;
            bottom: 0;
            height: 100%;
            padding: 0;
            transform: translateX(-100%);
            width: var(--sidebar-w);
          }
          .th-sidebar.open {
            transform: translateX(0);
            padding: 12px;
          }
          .th-sidebar.open .th-sidebar-panel {
            border-radius: 0 20px 20px 0;
          }
          .th-sidebar.collapsed { width: var(--sidebar-w); padding: 12px; }
          .th-sidebar-toggle { display: none; }
          .th-hamburger { display: flex; }
        }

        @media (max-width: 640px) {
          .th-topbar { padding: 0 14px; gap: 10px; }
          .th-search { display: none; }
          .th-breadcrumb-active { font-size: 13px; }
          .th-topbar-name, .th-topbar-chevron { display: none; }
        }
      `}</style>

      <div className="th-shell">
        {/* Mobile overlay */}
        <div className={`th-overlay${sidebarOpen ? ' open' : ''}`} onClick={() => setSidebarOpen(false)} />

        {/* ══ SIDEBAR ══ */}
        <nav className={`th-sidebar${sidebarOpen ? ' open' : ''}${collapsed ? ' collapsed' : ''}`}>
          <div className="th-sidebar-panel">

            {/* Brand */}
            <div className="th-brand">
              <div className="th-brand-mark">
                <img src="/logo.png" alt="Fabritech Logo" className="th-brand-logo" />
              </div>
              <div className="th-brand-text">
                <div className="th-brand-name">Fabri<em>tech</em></div>
                <div className="th-brand-tagline">Training Center</div>
              </div>
            </div>

            {/* User */}
            <div className="th-user-strip">
              <div className="th-user-av">
                {currentUser?.username?.[0]?.toUpperCase() || 'A'}
                <div className="th-user-av-online" />
              </div>
              <div className="th-user-info">
                <div className="th-user-name">{currentUser?.username || 'User'}</div>
                <span className="th-user-role">{formatRoleLabel(currentUser?.role)}</span>
              </div>
            </div>

            {/* Navigation */}
            <div className="th-nav">
              {currentNav.map((group) => (
                <div key={group.group} className="th-nav-group">
                  <div className="th-nav-group-label">{group.group}</div>
                  {group.items.map((item) => (
                    <button
                      key={item.path}
                      type="button"
                      className={`th-nav-item${isActive(item.path) ? ' active' : ''}`}
                      style={{ '--item-accent': item.accent || 'var(--accent)' }}
                      data-tip={item.label}
                      onClick={() => handleNav(item.path)}
                    >
                      <div className="th-nav-icon">{NAV_ICONS[item.icon] || NAV_ICONS.DB}</div>
                      <span className="th-nav-label">{item.label}</span>
                      {item.badge && <span className="th-nav-badge">{item.badge}</span>}
                    </button>
                  ))}
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="th-sidebar-footer">
              <button
                type="button"
                className="th-logout-btn"
                onClick={handleLogout}
                title="Sign out"
                data-tip="Sign out"
              >
                <span className="th-logout-icon" aria-hidden="true">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                </span>
                <span className="th-logout-label">Sign out</span>
              </button>
            </div>
          </div>
        </nav>

        {/* ══ MAIN ══ */}
        <div className="th-main">

          {/* Topbar */}
          <header className="th-topbar">
            {/* Mobile hamburger */}
            <button className="th-hamburger" onClick={() => setSidebarOpen(s => !s)}>≡</button>

            {/* Desktop sidebar toggle */}
            <button
              className="th-sidebar-toggle"
              onClick={() => setCollapsed(c => !c)}
              title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            >
              {collapsed ? '→' : '←'}
            </button>

            {/* Breadcrumb */}
            <div className="th-breadcrumb">
              <span>Fabritech</span>
              <span className="th-breadcrumb-sep">›</span>
              <span className="th-breadcrumb-active">
                <span className="th-breadcrumb-icon">{activeItem?.icon || 'DB'}</span>
                {activeItem?.label || 'Dashboard'}
              </span>
            </div>

            {/* Search */}
            <div className={`th-search${searchFocused ? ' focused' : ''}`}>
              <span className="th-search-icon">⌕</span>
              <input
                type="text"
                placeholder="Search anything..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
              />
              <span className="th-search-kbd">⌘K</span>
            </div>

            {/* Actions */}
            <div className="th-topbar-right">
              <button className="th-icon-btn" title="Notifications">
                ◉
                <span className="th-notif-dot" />
              </button>
              <button className="th-icon-btn" title="Toggle Theme" onClick={toggleTheme}>
                {theme === 'dark' ? '☀' : '☽'}
              </button>
              <div className="th-topbar-div" />
              <div className="th-topbar-user">
                <div className="th-topbar-av">{currentUser?.username?.[0]?.toUpperCase() || 'A'}</div>
                <span className="th-topbar-name">{currentUser?.username || 'Admin'}</span>
                <span className="th-topbar-chevron">▼</span>
              </div>
            </div>
          </header>

          {/* Page content */}
          <main className="th-content">
            {children}
          </main>
        </div>
      </div>
    </>
  );
};

export default TrainHubLayout;